import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { LucideIcon } from 'utils/lucideIcon'
import { showErrorMessage } from 'uiComponents/message'
import i18n, { Language, LANGUAGES, switchLanguage, useTranslation } from 'translations'
import './languageSelector.scss'

export function LanguageSelector() {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [switchingTo, setSwitchingTo] = useState<Language | null>(null)
    const rootRef = useRef<HTMLLIElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const current = LANGUAGES.find((language) => language.code === i18n.resolvedLanguage) ?? LANGUAGES[0]

    useEffect(() => {
        if (!open) {
            return
        }
        const closeOnOutsideClick = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setOpen(false)
            }
        }
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
                triggerRef.current?.focus()
            }
        }
        document.addEventListener('mousedown', closeOnOutsideClick)
        document.addEventListener('keydown', closeOnEscape)
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [open])

    const choose = async (language: Language) => {
        if (language === current.code) {
            setOpen(false)
            return
        }
        setSwitchingTo(language)
        try {
            // Reloads the page once the language is ready.
            await switchLanguage(language)
        } catch {
            showErrorMessage(t('sidebar.language.loadError'))
        } finally {
            setSwitchingTo(null)
            setOpen(false)
        }
    }

    return (
        <li ref={rootRef} className="language-selector">
            <button
                ref={triggerRef}
                type="button"
                className="menu-link language-selector-trigger"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={t('sidebar.language.triggerLabel', { language: current.name })}
                onClick={() => setOpen(!open)}
            >
                <div className="icon-wrapper">
                    <LucideIcon iconName="Languages" size={16} />
                </div>
                {current.name}
            </button>
            <ul className={classNames('language-selector-menu', { open })} role="menu" aria-busy={!!switchingTo}>
                {LANGUAGES.map((language) => (
                    <li key={language.code} role="none">
                        <button
                            type="button"
                            role="menuitemradio"
                            aria-checked={language.code === current.code}
                            lang={language.code}
                            className="language-selector-option"
                            disabled={!!switchingTo}
                            tabIndex={open ? 0 : -1}
                            onClick={() => choose(language.code)}
                        >
                            {language.name}
                            {language.code === current.code && <LucideIcon iconName="Check" size={16} />}
                            {language.code === switchingTo && (
                                <LucideIcon iconName="LoaderCircle" size={16} className="spinning" />
                            )}
                        </button>
                    </li>
                ))}
            </ul>
        </li>
    )
}
